// @owners { team: patient-fulfillment }
import {
  type BACKGROUND_COLORS_VALUES,
  type BaseIconName,
  COLORS,
  type ColorVariant,
} from '@alto/design-library-tokens';
import { Column, PresentationListItem, SmSpacing } from '@alto/design-system';
import React from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { CONFIRM } from '~shared/features/checkout/constants';
import getDefaultDeliveryWindow from '~shared/features/checkout/selectors/getDefaultDeliveryWindow';
import { getOrderAddress } from '~shared/features/checkout/selectors/getOrder';
import { type HomeToSignConfirmation } from '~shared/features/checkout/types';
import {
  getDeliveryDateText,
  getDeliveryMethod,
  getFormattedAddress,
} from '~shared/features/delivery-info/helpers/getShipmentWithItemsRowProps';
import { type APIOrderMethod } from '~shared/features/delivery-info/types';
import { isControl, isExpensive } from '~shared/helpers/helper';
import { isCourier, isMail } from '~shared/helpers/order';
import { useSelectorShared } from '~shared/store';
import { type Address, type Prescription } from '~shared/types';

type Props = {
  readonly date: string | null | undefined;
  readonly deliverAfter: string | null | undefined;
  readonly deliverBefore: string | null | undefined;
  readonly fullBleed?: boolean;
  readonly homeToSignConfirmation: HomeToSignConfirmation | null | undefined;
  readonly method: APIOrderMethod | null | undefined;
  readonly showMethod?: boolean;
  readonly showRefrigeratedInfo?: boolean;
  readonly status: string;
};

type GetInstructionsProps = {
  address: Address | null | undefined;
  homeToSignConfirmation: HomeToSignConfirmation | null | undefined;
  prescriptions: Prescription[];
  method: APIOrderMethod | null | undefined;
  status: string;
};
export const getInstructions = ({
  address,
  homeToSignConfirmation,
  prescriptions,
  method,
  status,
}: GetInstructionsProps): {
  iconName: BaseIconName;
  text: string;
  type?: ColorVariant;
  backgroundColor?: BACKGROUND_COLORS_VALUES;
} | null => {
  if (!address || ['delivered', 'failed'].includes(status)) {
    return {
      iconName: 'box',
      text: '',
    };
  }

  if (method === 'pickup') {
    return {
      iconName: 'box',
      text: "You're picking up this package",
      type: 'success',
    };
  }

  if (prescriptions.some((p) => isControl(p)) || (isExpensive(prescriptions) && homeToSignConfirmation === CONFIRM)) {
    return {
      iconName: 'signpaper',
      text: 'Someone needs to be present to sign for this delivery',
      type: 'grey',
      backgroundColor: COLORS.BACKGROUND_COLORS.WARNING_LIGHTEST,
    };
  }

  if (isMail(method)) {
    return null;
  }

  if (!address.safe_place) {
    return {
      iconName: 'box',
      text: 'Someone needs to be present to receive this delivery',
      type: 'grey',
      backgroundColor: COLORS.BACKGROUND_COLORS.WARNING_LIGHTEST,
    };
  }

  return {
    iconName: 'box',
    text: "We'll leave this package in your designated safe place",
    type: 'success',
  };
};

type DeliveryDateProps = {
  readonly date: string | null | undefined;
  readonly deliverBefore: string | null | undefined;
  readonly deliverAfter: string | null | undefined;
  readonly fullBleed: boolean;
  readonly method: APIOrderMethod | null | undefined;
};

const DeliveryDate = ({ date, deliverAfter, deliverBefore, fullBleed, method }: DeliveryDateProps) => {
  if (date) {
    return (
      <PresentationListItem
        iconName="calendar"
        fullBleed={fullBleed}
        text={getDeliveryDateText({
          deliverAfter,
          deliverBefore,
          date,
          method,
        })}
      />
    );
  }

  return (
    <PresentationListItem
      iconName="calendar"
      fullBleed={fullBleed}
      text="--"
    />
  );
};

export const DeliveryLogistics = ({
  date,
  deliverAfter: deliverAfterTime,
  deliverBefore: deliverBeforeTime,
  fullBleed = false,
  homeToSignConfirmation,
  method,
  showMethod = true,
  showRefrigeratedInfo = true,
  status,
}: Props) => {
  const address = useSelectorShared(getOrderAddress);
  const { prescriptions } = usePrescriptionsInCart();
  const defaultWindow = useSelectorShared(getDefaultDeliveryWindow);
  const isCourierDeliveryMethod = isCourier(method);

  const deliveryMethod = getDeliveryMethod(method, showMethod, status);
  const instructions = getInstructions({ address, homeToSignConfirmation, prescriptions, method, status });
  const requiresRefrigeration = prescriptions.some((p) => p.is_refrigerated);
  const hasAncillaryItems = prescriptions.some((p) => p.has_ancillary_items);

  // Check for valid delivery window, grab default window for courier shipment, or set window to null
  let [deliverAfter, deliverBefore] = [deliverAfterTime, deliverBeforeTime];
  if (!deliverAfter && !deliverBefore) {
    if (isCourierDeliveryMethod) {
      [deliverAfter, deliverBefore] = [defaultWindow?.deliver_after, defaultWindow?.deliver_before];
    } else {
      [deliverAfter, deliverBefore] = [null, null];
    }
  }

  return (
    <Column
      flexShrink={1}
      flexGrow={1}
    >
      <DeliveryDate
        date={date}
        deliverAfter={deliverAfter}
        deliverBefore={deliverBefore}
        fullBleed={fullBleed}
        method={method}
      />
      <PresentationListItem
        iconName="mapmarker"
        fullBleed={fullBleed}
        text={getFormattedAddress(address)}
      />
      {deliveryMethod.text ? (
        <PresentationListItem
          iconName={deliveryMethod.iconName}
          fullBleed={fullBleed}
          text={deliveryMethod.text}
        />
      ) : null}
      {instructions?.text ? (
        <PresentationListItem
          iconName={instructions.iconName}
          fullBleed={fullBleed}
          text={instructions.text}
          type={instructions.type}
          backgroundColor={instructions.backgroundColor}
        />
      ) : null}
      {showRefrigeratedInfo && requiresRefrigeration ? (
        <PresentationListItem
          iconName="snowflake"
          fullBleed={fullBleed}
          text="Refrigeration required"
        />
      ) : null}
      {hasAncillaryItems ? (
        <PresentationListItem
          iconName="syringe"
          fullBleed={fullBleed}
          text="Ancillary items included"
        />
      ) : null}
      <SmSpacing />
    </Column>
  );
};
